import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import fr from 'date-fns/locale/fr'
registerLocale('fr', fr)

import { Document } from '../../../domain/documents.types'
import {
  DASHBOARD_DATA_TYPE_DISPLAYED,
  DASHBOARD_GRAPH_UNIT,
  DASHBOARD_PERIOD,
} from '../../../domain/enums/dashboard.enum'
import Graph from '../../components/graph/graph'
import PlantSearchRedirect from '../../components/plantSearchRedirect/plantSearchRedirect'
import { PlantSearchSelected } from '../../components/plantSearchRedirect/plantSearchSelected'
import ShortListDocuments from '../../components/shortListDocuments/shortListDocuments'
import Statistic from '../../components/statistic/statistic'
import { useDashBoardHook } from './dashboard.hook'
import {
  CenterWrapper,
  CustomInput,
  DashboardWrapper,
  DatePickerWrapper,
  DateSelectorButtonWrapper,
  DateSelectorWrapper,
  DocumentShortListWrapper,
  GraphCardWrapper,
  GraphWrapper,
  GroupButton,
  HeaderWrapper,
  InnerGroupButton,
  PeriodSelectorWrapper,
  StatsWrapper,
  Title,
} from './dashboard.styles'

import 'react-datepicker/dist/react-datepicker.css'

const Dashboard: React.FC = () => {
  const {
    isMobile,
    selectedPlant,
    selectedDate,
    optionsDatePicker,
    userAccessState,
    salesRevenue,
    energyGraphPoints,
    expectedProduction,
    displayPowerOrEnergy,
    documents,
    analyticsDataLoader,
    graphDataLoader,
    formatedData,
    periodSelected,
    interval,
    isGlobalLoading,
    t,
    graphDataBuilder,
    handleChangePeriod,
    selectDateFromDatePicker,
    handleDisplayEnergyOrPower,
  } = useDashBoardHook()

  return (
    <DashboardWrapper>
      {!isMobile && <PlantSearchRedirect />}
      <HeaderWrapper>
        {!isMobile && (
          <Title>
            <PlantSearchSelected selectedPlant={selectedPlant} title={t('overview')} />
          </Title>
        )}
        <DateSelectorWrapper>
          <PeriodSelectorWrapper>
            <DateSelectorButtonWrapper
              active={periodSelected === DASHBOARD_PERIOD.DAY}
              onClick={() => handleChangePeriod(DASHBOARD_PERIOD.DAY)}
              disabled={!isGlobalLoading}
            >
              {t('1d', 'uppercase')}
            </DateSelectorButtonWrapper>
            <DateSelectorButtonWrapper
              active={periodSelected === DASHBOARD_PERIOD.WEEK}
              onClick={() => handleChangePeriod(DASHBOARD_PERIOD.WEEK)}
              disabled={!isGlobalLoading}
            >
              {t('7d', 'uppercase')}
            </DateSelectorButtonWrapper>
            <DateSelectorButtonWrapper
              active={periodSelected === DASHBOARD_PERIOD.MONTH}
              onClick={() => handleChangePeriod(DASHBOARD_PERIOD.MONTH)}
              disabled={!isGlobalLoading}
            >
              {t('1m', 'uppercase')}
            </DateSelectorButtonWrapper>
            <DateSelectorButtonWrapper
              active={periodSelected === DASHBOARD_PERIOD.YEAR}
              onClick={() => handleChangePeriod(DASHBOARD_PERIOD.YEAR)}
              disabled={!isGlobalLoading}
            >
              {t('1a', 'uppercase')}
            </DateSelectorButtonWrapper>
          </PeriodSelectorWrapper>
          <DatePickerWrapper>
            <DatePicker
              selected={new Date(selectedDate)}
              onChange={(date) =>
                date !== null && selectDateFromDatePicker(new Date(date).valueOf())
              }
              disabled={!isGlobalLoading}
              locale="fr"
              {...optionsDatePicker}
              customInput={<CustomInput />}
              placeholderText={t('select_date')}
            />
          </DatePickerWrapper>
        </DateSelectorWrapper>
      </HeaderWrapper>

      <StatsWrapper>
        {userAccessState?.dashboardProductionStat && (
          <Statistic
            data={formatedData}
            title={t('production', 'uppercase')}
            type="sumProdReal"
            units="kWh"
            infoMessage={t('establish_bill_refer_to_meter_reading')}
            isLoading={graphDataLoader.energy}
            isMobile={isMobile}
          />
        )}
        {userAccessState?.dashboardCaStat && (
          <Statistic
            data={salesRevenue}
            title={t('gross_sales', 'uppercase')}
            type="CA"
            units="€"
            isLoading={analyticsDataLoader.revenue}
            isMobile={isMobile}
          />
        )}
      </StatsWrapper>
      {userAccessState?.dashboardGraph && (
        <GraphCardWrapper>
          {graphDataLoader.graph ? (
            <CenterWrapper>
              <FontAwesomeIcon icon="spinner" spin size="lg" />
              <p>{`${t('collect_your_production_data')}...`}</p>
            </CenterWrapper>
          ) : (
            <GraphWrapper>
              <Graph
                data={graphDataBuilder()}
                units={
                  displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.POWER
                    ? DASHBOARD_GRAPH_UNIT.KW
                    : DASHBOARD_GRAPH_UNIT.KWH
                }
                interval={interval}
              />
              {periodSelected === DASHBOARD_PERIOD.DAY && (
                <GroupButton>
                  <InnerGroupButton
                    style={{ borderRadius: '4px 0 0 4px' }}
                    title={t('energy')}
                    selected={displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.ENERGY}
                    onClick={() => handleDisplayEnergyOrPower(DASHBOARD_DATA_TYPE_DISPLAYED.ENERGY)}
                  >
                    <FontAwesomeIcon icon="chart-bar" size="lg" />
                  </InnerGroupButton>
                  <InnerGroupButton
                    style={{ borderRadius: '0 4px 4px 0' }}
                    title={t('power')}
                    selected={displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.POWER}
                    onClick={() => handleDisplayEnergyOrPower(DASHBOARD_DATA_TYPE_DISPLAYED.POWER)}
                    borderRight
                  >
                    <FontAwesomeIcon icon="chart-line" size="lg" />
                  </InnerGroupButton>
                </GroupButton>
              )}
            </GraphWrapper>
          )}
          {!analyticsDataLoader.documents &&
            energyGraphPoints.length === 0 &&
            expectedProduction.length === 0 && (
              <CenterWrapper>
                <FontAwesomeIcon icon="exclamation-triangle" size="lg" />
                <p>{t('no_data_for_this_period')}</p>
              </CenterWrapper>
            )}
        </GraphCardWrapper>
      )}
      {!isMobile && userAccessState?.dashboardDocumentList && documents && (
        <DocumentShortListWrapper>
          <ShortListDocuments
            documentsList={documents as Document[]}
            documentsLoading={analyticsDataLoader.documents}
          />
        </DocumentShortListWrapper>
      )}
    </DashboardWrapper>
  )
}

export default Dashboard
